import { useState, useEffect } from "react";
import { type SiteWithState, type Pin, api } from "../api";
import {
  MKCard,
  MKCardHeader,
  MKCardTitle,
  MKButton,
  MKInput,
  MKLabel,
  MKDialog,
  MKDialogContent,
  MKDialogHeader,
  MKDialogTitle,
  MKDialogFooter,
  MKSelect,
  MKSelectTrigger,
  MKSelectValue,
  MKSelectContent,
  MKSelectItem,
  useToast,
} from "@mytraai/mytrakit";
import { SearchSelect } from "./SearchSelect";

const ANY_BOT = "$any";

interface Props {
  site: SiteWithState;
  onUpdate: () => void;
}

export default function PinEditor({ site, onUpdate }: Props) {
  const toast = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPin, setEditingPin] = useState<Pin | null>(null);
  const [appName, setAppName] = useState("");
  const [botId, setBotId] = useState<string>(ANY_BOT);
  const [branch, setBranch] = useState("");
  const [version, setVersion] = useState("");

  const isEditing = editingPin !== null;
  const hasBots = site.bots.length > 0;

  const getBotName = (id: number | null): string => {
    if (id === null) return "";
    const bot = site.bots.find((b) => b.bot_id === id);
    return bot?.name ?? "";
  };

  const openAddDialog = () => {
    setEditingPin(null);
    setAppName("");
    setBotId(ANY_BOT);
    setBranch("");
    setVersion("");
    setDialogOpen(true);
  };

  const openEditDialog = (pin: Pin) => {
    setEditingPin(pin);
    setAppName(pin.app);
    setBotId(pin.bot_id !== null ? String(pin.bot_id) : ANY_BOT);
    setBranch(pin.branch || "");
    setVersion(pin.version || "");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPin(null);
    setAppName("");
    setBotId(ANY_BOT);
    setBranch("");
    setVersion("");
  };

  const handleSavePin = async () => {
    try {
      const parsedBotId = botId === ANY_BOT ? null : parseInt(botId, 10);
      const pinId = editingPin?.pin_id ?? null;
      await api.upsertPin(
        site.site_id,
        appName,
        parsedBotId,
        branch,
        version,
        pinId,
      );
      toast({
        title: "Pin saved",
        description: `${appName} pinned to ${version}`,
      });
      closeDialog();
      onUpdate();
    } catch (err) {
      toast.error({
        title: "Failed to save pin",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  const handleDeletePin = async () => {
    if (!editingPin) return;
    try {
      await api.deletePin(site.site_id, editingPin.app, editingPin.bot_id);
      toast({
        title: "Pin deleted",
        description: `Removed pin for ${editingPin.app}`,
      });
      closeDialog();
      onUpdate();
    } catch (err) {
      toast.error({
        title: "Failed to delete pin",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  };

  return (
    <MKCard>
      <MKCardHeader className="border-b">
        <MKCardTitle>Pins</MKCardTitle>
      </MKCardHeader>

      {site.pins.length > 0 ? (
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-surface-tertiary text-left text-sm text-content-tertiary">
              <th className="p-3 w-1/4">App</th>
              {hasBots && <th className="p-3 w-1/6">Bot</th>}
              <th className="p-3">Branch</th>
              <th className="p-3">Version</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-tertiary">
            {site.pins.map((pin) => (
              <tr
                key={pin.pin_id}
                className="hover:bg-surface-secondary cursor-pointer transition-colors"
                onClick={() => openEditDialog(pin)}
              >
                <td className="p-3 font-medium text-content-primary truncate">
                  {pin.app}
                </td>
                {hasBots && (
                  <td className="p-3 text-content-secondary truncate">
                    {getBotName(pin.bot_id)}
                  </td>
                )}
                <td className="p-3 text-content-secondary truncate">
                  {pin.branch}
                </td>
                <td className="p-3 truncate">
                  <code className="text-sm bg-surface-tertiary px-1.5 py-0.5 rounded">
                    {pin.version}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="p-4 text-content-tertiary text-center">
          No pins configured
        </div>
      )}

      <div className="p-4 border-t border-surface-tertiary">
        <MKButton
          variant="secondary"
          className="w-full"
          onClick={openAddDialog}
        >
          Add Pin
        </MKButton>
      </div>

      <MKDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <MKDialogContent>
          <MKDialogHeader>
            <MKDialogTitle>
              {isEditing ? "Edit Version Pin" : "Add Version Pin"}
            </MKDialogTitle>
          </MKDialogHeader>
          <PinForm
            site={site}
            appName={appName}
            botId={botId}
            branch={branch}
            version={version}
            onAppNameChange={setAppName}
            onBotIdChange={setBotId}
            onBranchChange={setBranch}
            onVersionChange={setVersion}
            isEditing={isEditing}
          />
          <MKDialogFooter>
            {isEditing ? (
              <>
                <MKButton variant="destructive" onClick={handleDeletePin}>
                  Delete
                </MKButton>
                <div className="flex-1" />
                <MKButton variant="secondary" onClick={closeDialog}>
                  Cancel
                </MKButton>
                <MKButton
                  onClick={handleSavePin}
                  disabled={!appName || !branch || !version}
                >
                  Save
                </MKButton>
              </>
            ) : (
              <>
                <MKButton variant="secondary" onClick={closeDialog}>
                  Cancel
                </MKButton>
                <MKButton
                  onClick={handleSavePin}
                  disabled={!appName || !branch || !version}
                >
                  Add Pin
                </MKButton>
              </>
            )}
          </MKDialogFooter>
        </MKDialogContent>
      </MKDialog>
    </MKCard>
  );
}

interface PinFormProps {
  site: SiteWithState;
  appName: string;
  botId: string;
  branch: string;
  version: string;
  onAppNameChange: (value: string) => void;
  onBotIdChange: (value: string) => void;
  onBranchChange: (value: string) => void;
  onVersionChange: (value: string) => void;
  isEditing: boolean;
}

function PinForm({
  site,
  appName,
  botId,
  branch,
  version,
  onAppNameChange,
  onBotIdChange,
  onBranchChange,
  onVersionChange,
  isEditing,
}: PinFormProps) {
  const [appNames, setAppNames] = useState<string[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [versions, setVersions] = useState<string[]>([]);

  const appIsDeployedToBots = appName
    ? site.bots.some(
        (bot) =>
          bot.current_artifacts?.[appName] || bot.pending_artifact?.[appName],
      )
    : false;

  useEffect(() => {
    if (!isEditing) {
      api
        .listAppNames()
        .then((names) => setAppNames(names ?? []))
        .catch(console.error);
    }
  }, [isEditing]);

  useEffect(() => {
    if (appName) {
      api
        .listBranches(appName)
        .then((b) => setBranches(b ?? []))
        .catch(console.error);
    } else {
      setBranches([]);
    }
  }, [appName]);

  useEffect(() => {
    if (appName && branch) {
      api
        .listVersions(appName, branch)
        .then((v) => setVersions(["$latest", ...(v ?? [])]))
        .catch(console.error);
    } else {
      setVersions([]);
    }
  }, [appName, branch]);

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <MKLabel>App Name</MKLabel>
        {isEditing ? (
          <MKInput
            type="text"
            value={appName}
            disabled
            className="bg-surface-secondary"
          />
        ) : (
          <SearchSelect
            value={appName}
            options={appNames}
            placeholder="Select an app..."
            searchPlaceholder="Search apps..."
            emptyMessage="No apps found"
            onValueChange={(value) => {
              onAppNameChange(value);
              onBranchChange("");
              onVersionChange("");
            }}
          />
        )}
      </div>
      {appIsDeployedToBots && (
        <div className="space-y-2">
          <MKLabel>Bot</MKLabel>
          <MKSelect value={botId} onValueChange={onBotIdChange}>
            <MKSelectTrigger>
              <MKSelectValue placeholder="Select a bot..." />
            </MKSelectTrigger>
            <MKSelectContent>
              <MKSelectItem value={ANY_BOT}>Any</MKSelectItem>
              {site.bots.map((bot) => (
                <MKSelectItem key={bot.bot_id} value={String(bot.bot_id)}>
                  {bot.name}
                </MKSelectItem>
              ))}
            </MKSelectContent>
          </MKSelect>
        </div>
      )}
      <div className="space-y-2">
        <MKLabel>Branch</MKLabel>
        <SearchSelect
          value={branch}
          options={branches}
          placeholder={appName ? "Select a branch..." : "Select an app first"}
          searchPlaceholder="Search branches..."
          emptyMessage={appName ? "No branches found" : "Select an app first"}
          disabled={!appName}
          onValueChange={(value) => {
            onBranchChange(value);
            onVersionChange("$latest");
          }}
        />
      </div>
      <div className="space-y-2">
        <MKLabel>Version</MKLabel>
        <SearchSelect
          value={version}
          options={versions}
          placeholder={branch ? "Select a version..." : "Select a branch first"}
          searchPlaceholder="Search versions..."
          emptyMessage={branch ? "No versions found" : "Select a branch first"}
          disabled={!branch}
          onValueChange={onVersionChange}
        />
      </div>
    </div>
  );
}
